import { useState, type FormEvent } from "react";
import { leakCategories } from "@/mocks/leaks";

const SUBMIT_ADDR = "https://readdy.ai/api/form/dah2rkboh653ivfvnkkg";

type Status = "idle" | "submitting" | "success" | "error";

export default function SubmitForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const raw = new FormData(form);

    const payload = new URLSearchParams();
    raw.forEach((value, key) => {
      if (key === "contact_alt") return;
      const text = typeof value === "string" ? value : "";
      if (text.trim() === "") return;
      payload.append(key, text);
    });

    const trapValue = String(raw.get("contact_alt") ?? "").trim();
    if (trapValue !== "") {
      setStatus("success");
      setFormError("");
      form.reset();
      setCharCount(0);
      return;
    }

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText;
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        setFormError("");
        form.reset();
        setCharCount(0);
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交未成功，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，投稿未能送达，请稍后再试。");
    }
  };

  return (
    <form
      id="submit"
      data-readdy-form="true"
      action={SUBMIT_ADDR}
      method="post"
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7"
    >
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <div className="flex flex-col gap-2">
          <label htmlFor="category" className="font-label text-sm font-medium text-foreground-800">
            线索分类 <span className="text-primary-600">*</span>
          </label>
          <select
            id="category"
            name="category"
            required
            defaultValue=""
            className="h-11 rounded-md border border-background-300 bg-background-50 px-3 font-label text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 cursor-pointer"
          >
            <option value="" disabled>
              请选择线索所属分类
            </option>
            {leakCategories.map((cat) => (
              <option key={cat.slug} value={cat.name}>
                {cat.name}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="title" className="font-label text-sm font-medium text-foreground-800">
            线索标题 <span className="text-primary-600">*</span>
          </label>
          <input
            id="title"
            name="title"
            type="text"
            required
            maxLength={80}
            placeholder="用一句话概括你掌握的情况"
            className="h-11 rounded-md border border-background-300 bg-background-50 px-3 font-label text-sm text-foreground-900 placeholder:text-foreground-400 outline-none transition-colors focus:border-primary-400"
          />
        </div>
      </div>

      <div className="mt-5 flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <label htmlFor="detail" className="font-label text-sm font-medium text-foreground-800">
            线索详情 <span className="text-primary-600">*</span>
          </label>
          <span className="font-label text-xs text-foreground-400">{charCount} / 500</span>
        </div>
        <textarea
          id="detail"
          name="detail"
          required
          maxLength={500}
          rows={6}
          onChange={(e) => setCharCount(e.target.value.length)}
          placeholder="时间、地点、涉及环节、你观察到的具体细节。请勿填写个人隐私信息，我们只记录与线索相关的事实描述。"
          className="rounded-md border border-background-300 bg-background-50 p-3 font-label text-sm leading-relaxed text-foreground-900 placeholder:text-foreground-400 outline-none transition-colors focus:border-primary-400"
        />
      </div>

      <div className="mt-5 flex flex-col gap-2">
        <label htmlFor="email" className="font-label text-sm font-medium text-foreground-800">
          联系邮箱（可选）
        </label>
        <input
          id="email"
          name="email"
          type="email"
          placeholder="如需我们回访核实，可留下邮箱；不填亦可提交"
          className="h-11 rounded-md border border-background-300 bg-background-50 px-3 font-label text-sm text-foreground-900 placeholder:text-foreground-400 outline-none transition-colors focus:border-primary-400"
        />
      </div>

      <div className="field-supplement-aux" aria-hidden="true">
        <label htmlFor="contact_alt">补充联系方式</label>
        <input
          id="contact_alt"
          name="contact_alt"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          readOnly
        />
      </div>

      {status === "error" ? (
        <p className="mt-4 flex items-start gap-2 rounded-md border border-primary-200 bg-primary-100/70 px-3 py-2 font-label text-sm text-primary-800">
          <span className="mt-0.5 flex h-4 w-4 items-center justify-center">
            <i className="ri-error-warning-line" />
          </span>
          {formError}
        </p>
      ) : null}

      {status === "success" ? (
        <p className="mt-4 flex items-start gap-2 rounded-md border border-accent-300 bg-accent-100/80 px-3 py-2 font-label text-sm text-accent-900">
          <span className="mt-0.5 flex h-4 w-4 items-center justify-center">
            <i className="ri-checkbox-circle-line" />
          </span>
          投稿已收到，夜班编辑将在 24 小时内完成初步整理与分级。谢谢你愿意开口。
        </p>
      ) : null}

      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="font-label text-xs leading-relaxed text-foreground-500">
          来源信息默认脱敏处理，未经你同意不会对外披露任何可识别身份的内容。
        </p>
        <button
          type="submit"
          disabled={status === "submitting"}
          className="btn-base bg-primary-500 px-6 py-3 text-background-50 hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <i className="ri-send-plane-2-line text-base" />
          {status === "submitting" ? "正在投递…" : "匿名投递线索"}
        </button>
      </div>
    </form>
  );
}